import React, { useCallback, useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import { GetColor, getItemLabel, Icon, Notifications, NotificationType, SwapArrowsIcon } from 'venn-ui-kit';
import { AskVennToggle, SaveAnalysisViewModal, ShareModal, StudioContext, UserContext } from 'venn-components';
import { analyticsService, copyToClipboard, Numbers, useHasFF, useModal } from 'venn-utils';
import { InlineInput } from '../pages/Shared';
import { getTopBarColor, type GetTopBarColorType, topBarButton, TopBarButtonLabel, TOP_TOOLBAR_HEIGHT } from './shared';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  analysisViewIdState,
  analysisViewNameState,
  analysisViewOwnerContextIdState,
  analysisViewTypeState,
  exportingToExcel,
  hasUnsavedChangesState,
  isReportState,
  isTemplateState,
  PageLayout,
  reportPageLayout,
  reportZoom,
  useRecoilValueWithDefault,
} from 'venn-state';
import { useRedistributableNotification } from '../../logic/useRedistributableNotification';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { RedesignedPdfExport } from './RedesignedPdfExport';
import { Tooltip, TooltipContentWithArrow, TooltipTrigger } from '@/components/ui/tooltip';
import { AllocationsButton } from './AllocationsButton';

interface TopBarProps {
  openReportConfigModal?: () => void;
}

export const RedesignedTopBar = React.memo(function RedesignedTopBar({ openReportConfigModal }: TopBarProps) {
  const hasAskVenn = useHasFF('ask_venn_ff');
  const [zoom, setZoom] = useRecoilState(reportZoom);
  const [layout, setLayout] = useRecoilState(reportPageLayout);
  const isExportingExcel = useRecoilValue(exportingToExcel);
  const { hasPermissionForResource, currentContext } = useContext(UserContext);
  const {
    onSave: onSaveInput,
    onExcelExport,
    reportNameValue,
    onChangeReportName,
    onSaveAs,
    isCheckingDuplicateReportName,
    isDuplicateReportName,
    isFetchingNewViewRef,
    setAfterUnsavedChangesAction,
  } = useContext(StudioContext);
  const viewId = useRecoilValue(analysisViewIdState);
  const viewName = useRecoilValue(analysisViewNameState);
  const analysisViewType = useRecoilValue(analysisViewTypeState);
  const ownerContextId = useRecoilValue(analysisViewOwnerContextIdState);
  const isReport = useRecoilValue(isReportState);
  const isTemplate = useRecoilValue(isTemplateState);
  const barColors = getTopBarColor({ isReport, isTemplate });
  const hasUnsavedChanges = useRecoilValueWithDefault(hasUnsavedChangesState, false);

  useRedistributableNotification();

  const [isSaving, setIsSaving] = useState(false);

  const onSave = useCallback(async () => {
    setIsSaving(true);
    await onSaveInput?.();
    setIsSaving(false);
  }, [onSaveInput]);

  const [isOpenShareEmailModal, openShareEmailModal, closeShareEmailModal] = useModal();
  const [isOpenSaveAsModal, openSaveAsModal, closeSaveAsModal] = useModal();

  const canSave = hasPermissionForResource(isTemplate ? 'STUDIO_CREATE_TEMPLATE' : 'EDIT_ANALYSIS_VIEW', {
    ownerContextId: ownerContextId ?? currentContext,
  });

  // Save as would use current context as default, check if has permission
  const canSaveAs = hasPermissionForResource('CREATE_STUDIO_VIEW', {
    ownerContextId: currentContext,
  });

  const handleCopyLink = async () => {
    const response = await copyToClipboard(window.location.href);
    if (response) {
      Notifications.notify('Link copied', NotificationType.SUCCESS);
      analyticsService.ctaClicked({
        purpose: isReport ? 'copy Report Lab Link' : 'copy studio link',
        locationOnPage: isReport ? 'Report Lab Dropdown' : 'studio share dropdown',
      });
    } else {
      Notifications.notify('Problem copying link', NotificationType.ERROR);
    }
  };

  const handleShareAction = (proceedCallback: () => void) => {
    if (hasUnsavedChanges) {
      setAfterUnsavedChangesAction({
        proceedCallback,
        cancelCallback: undefined,
        hideDiscardBtn: !viewId,
      });
    } else {
      proceedCallback();
    }
  };

  const onZoom = useCallback(
    (zoom: number, ratio: number) => {
      analyticsService.ctaClicked({
        purpose: `zoom ${ratio > 0 ? 'in' : 'out'}`,
        locationOnPage: 'studio topbar',
      });
      setZoom(zoom + ratio);
    },
    [setZoom],
  );

  const onLayoutChange = useCallback(
    (layout: PageLayout) => {
      analyticsService.ctaClicked({
        purpose: `change layout to ${layout}`,
        locationOnPage: 'studio topbar',
      });
      setLayout(layout);
    },
    [setLayout],
  );

  const onZoomOut = useCallback(() => onZoom(zoom, -0.1), [onZoom, zoom]);
  const onZoomIn = useCallback(() => onZoom(zoom, 0.1), [onZoom, zoom]);

  return (
    <>
      <div data-testid="qa-top-bar" className="flex items-center justify-between h-[65px] bg-venn-grey-90">
        <AllocationsButton />
        <button
          type="button"
          className={topBarButton()}
          onClick={openReportConfigModal}
          disabled={isFetchingNewViewRef.current}
          data-testid="setup"
        >
          <TopBarButtonLabel>Setup</TopBarButtonLabel>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="text-white h-full hover:bg-venn-grey-80 aria-expanded:font-bold aria-expanded:bg-white aria-expanded:text-venn-dark-blue hover:text-white group"
              disabled={isFetchingNewViewRef.current}
              data-testid="save-view-dropdown"
            >
              <TopBarButtonLabel>
                Save
                <Icon type="caret-down" prefix="fas" />
              </TopBarButtonLabel>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuPortal>
            <DropdownMenuContent className="w-[168px] text-white bg-venn-grey-90">
              <DropdownMenuItem
                className="focus:bg-venn-grey-80 focus:text-white text-lg"
                disabled={
                  !hasUnsavedChanges ||
                  isSaving ||
                  isDuplicateReportName ||
                  isCheckingDuplicateReportName ||
                  isFetchingNewViewRef.current ||
                  !canSave
                }
                onSelect={onSave}
                data-testid="save-view"
              >
                Save
              </DropdownMenuItem>
              <DropdownMenuItem
                className="focus:bg-venn-grey-80 focus:text-white text-lg"
                disabled={isFetchingNewViewRef.current || !viewId || !canSaveAs}
                onSelect={openSaveAsModal}
                data-testid="save-view-as"
              >
                Save As
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenuPortal>
        </DropdownMenu>

        <div className="flex items-center lg:ml-[25px] flex-1 overflow-hidden">
          {isTemplate && (
            <TemplateLabelWrapper barColors={barColors}>{getItemLabel(analysisViewType)}</TemplateLabelWrapper>
          )}
          <div className="flex-1 overflow-hidden">
            <DocumentNameLabel
              $inPrintMode={false}
              placeholder="Untitled Analysis"
              value={reportNameValue}
              onChange={onChangeReportName}
              $isDuplicateReportName={isDuplicateReportName}
              disabled={isFetchingNewViewRef.current}
              $barColors={barColors}
              extraWidth={0}
            />
          </div>
        </div>
        {isReport && (
          <div className="flex gap-[15px] px-[18px]">
            <div className="flex items-center gap-[6px]">
              <ToggleGroup
                id="layouts"
                type="single"
                value={layout}
                onValueChange={onLayoutChange}
                disabled={isFetchingNewViewRef.current}
                className="h-[40px] gap-0 border border-solid border-venn-grey-30 rounded-lg overflow-hidden"
                data-testid="page-display-toggle-group"
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <ToggleGroupItem
                        aria-label="Toggle horizontal page display"
                        value={PageLayout.HORIZONTAL}
                        disabled={layout === PageLayout.HORIZONTAL}
                        className="h-[40px] w-[40px] rounded-none text-xl text-white hover:bg-venn-grey-80 hover:text-white data-[state=on]:opacity-100 data-[state=on]:bg-white data-[state=on]:text-venn-dark-blue"
                      >
                        <Icon type="arrows-h" />
                      </ToggleGroupItem>
                    </div>
                  </TooltipTrigger>
                  <TooltipContentWithArrow side="bottom">Page display: horizontal</TooltipContentWithArrow>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <ToggleGroupItem
                        aria-label="Toggle side by side page display"
                        value={PageLayout.COLUMNS}
                        disabled={layout === PageLayout.COLUMNS}
                        className="h-[40px] w-[40px] rounded-none text-xl text-white hover:bg-venn-grey-80 hover:text-white data-[state=on]:opacity-100 data-[state=on]:bg-white data-[state=on]:text-venn-dark-blue"
                      >
                        <SwapArrowsIcon className="!h-auto !w-[32px]" />
                      </ToggleGroupItem>
                    </div>
                  </TooltipTrigger>
                  <TooltipContentWithArrow side="bottom">Page display: side-by-side</TooltipContentWithArrow>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <ToggleGroupItem
                        aria-label="Toggle vertical page display"
                        value={PageLayout.VERTICAL}
                        disabled={layout === PageLayout.VERTICAL}
                        className="h-[40px] w-[40px] rounded-none text-xl text-white hover:bg-venn-grey-80 hover:text-white data-[state=on]:opacity-100 data-[state=on]:bg-white data-[state=on]:text-venn-dark-blue"
                      >
                        <Icon type="arrows-v" />
                      </ToggleGroupItem>
                    </div>
                  </TooltipTrigger>
                  <TooltipContentWithArrow side="bottom">Page display: vertical</TooltipContentWithArrow>
                </Tooltip>
              </ToggleGroup>
            </div>
            <div className="flex gap-[3px] border border-solid border-venn-grey-30 rounded-lg overflow-hidden">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="h-[40px] w-[40px] text-xl flex items-center px-[12px] hover:bg-venn-grey-80 text-white hover:text-white"
                    aria-label="Zoom out"
                    disabled={zoom < 0.15 || isFetchingNewViewRef.current}
                    onClick={onZoomOut}
                  >
                    <Icon type="search-minus" prefix="far" />
                  </button>
                </TooltipTrigger>
                <TooltipContentWithArrow side="bottom">Zoom Out</TooltipContentWithArrow>
              </Tooltip>
              <div
                className="flex items-center justify-center w-[40px] text-white"
                role="spinbutton"
                aria-valuenow={zoom}
                aria-valuemin={0.1}
                aria-valuemax={2}
                aria-label="Zoom"
              >
                {Numbers.safeFormatPercentage(zoom, 0)}
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="h-[40px] w-[40px] text-xl flex items-center px-[12px] hover:bg-venn-grey-80 text-white hover:text-white"
                    aria-label="Zoom in"
                    disabled={zoom > 1.95 || isFetchingNewViewRef.current}
                    onClick={onZoomIn}
                  >
                    <Icon type="search-plus" prefix="far" />
                  </button>
                </TooltipTrigger>
                <TooltipContentWithArrow side="bottom">Zoom In</TooltipContentWithArrow>
              </Tooltip>
            </div>
          </div>
        )}
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex gap-[6px] items-center justify-center text-white h-[64px] w-[64px] hover:bg-venn-grey-80 aria-expanded:bg-white aria-expanded:text-venn-dark-blue hover:text-white"
                  disabled={isFetchingNewViewRef.current}
                  aria-label="Share options"
                >
                  <Icon type="share-alt" prefix="far" className="text-2xl" />
                  <Icon type="caret-down" prefix="fas" />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContentWithArrow side="bottom">Share Report</TooltipContentWithArrow>
          </Tooltip>
          <DropdownMenuPortal>
            <DropdownMenuContent className="w-[168px] text-white bg-venn-grey-90">
              <DropdownMenuItem
                className="focus:bg-venn-grey-80 focus:text-white text-lg"
                onSelect={() => handleShareAction(openShareEmailModal)}
              >
                <Icon type="envelope" prefix="far" />
                Email
              </DropdownMenuItem>
              <DropdownMenuItem
                className="focus:bg-venn-grey-80 focus:text-white text-lg"
                onSelect={() => handleShareAction(handleCopyLink)}
              >
                <Icon type="link" prefix="far" />
                Copy URL
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenuPortal>
        </DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="flex gap-[6px] items-center justify-center text-white h-[64px] w-[64px] hover:bg-venn-grey-80 hover:text-white"
              onClick={onExcelExport}
              disabled={isFetchingNewViewRef.current || isExportingExcel}
              aria-label="Export to excel"
            >
              <Icon type="file-excel" className="text-2xl" />
            </button>
          </TooltipTrigger>
          <TooltipContentWithArrow side="bottom">Excel Export</TooltipContentWithArrow>
        </Tooltip>
        <RedesignedPdfExport />
        {hasAskVenn && <AskVennToggle />}
      </div>
      {isOpenShareEmailModal && (
        <ShareModal onClose={closeShareEmailModal} shareMetaData={{ savedViewName: viewName }} closeOnOutsideClick />
      )}
      {isOpenSaveAsModal && (
        <SaveAnalysisViewModal
          mode="SAVE_AS"
          defaultName={`${viewName} (Copy)`}
          onSubmit={onSaveAs}
          onClose={closeSaveAsModal}
        />
      )}
    </>
  );
});

const DocumentNameLabel = styled(InlineInput)<{ $isDuplicateReportName?: boolean; $barColors: GetTopBarColorType }>`
  padding-left: 6px;
  padding-right: 25px;
  overflow: hidden;
  font-weight: bold;
  font-size: 20px;
  color: ${GetColor.White};

  text-overflow: ellipsis;
  max-width: calc(100% - 35px);
  margin: 1px;
  flex: 1;

  input:disabled {
    color: ${({ $barColors }) => $barColors.DarkGrey};
  }

  ${({ $isDuplicateReportName }) =>
    $isDuplicateReportName &&
    css`
      color: ${GetColor.Error};
      outline: 1px solid ${GetColor.Error};
      :hover {
        outline: 1px solid ${GetColor.Alert};
      }
      :focus {
        outline: 1px solid ${GetColor.Alert};
      }
    `}
`;

const TemplateLabelWrapper = styled.div<{ barColors: GetTopBarColorType }>`
  /* gold-dark */
  background: ${({ barColors }) => barColors.ActiveBackground};
  border: 1px solid ${GetColor.White};
  color: ${GetColor.White};
  box-sizing: border-box;
  border-radius: 2px;
  margin-right: 8px;
  padding: 5px;
  height: 22px;

  font-weight: 700;
  font-size: 12px;
  line-height: 14px;
  display: flex;
  align-items: center;
`;

export const TopBarWrapper = styled.div<{ barColors: GetTopBarColorType }>`
  height: ${TOP_TOOLBAR_HEIGHT};
  background: ${({ barColors }) => barColors.Background};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
